import { Fragment, useMemo } from 'react';
import { Box, Typography, Grid, Tooltip, Link, Paper } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TooltipTitle from './Calendar/TooltipTitle';
import { generateTimeSlots } from './Calendar/utils';
import { links_styles, status_icon_styles } from './Calendar/styles';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

const WeekView = ({ 
  date, 
  appointments, 
  technicians,
  workHours = { startHour: 7, endHour: 22 }
}) => {
  // moment.tz.setDefault is now configured in dateUtils.js
  const timeSlots = useMemo(() => generateTimeSlots(workHours.startHour, workHours.endHour), [workHours]);
  
  // Calculate week days (Mon-Sun)
  const weekDays = useMemo(() => {
    const startOfWeek = moment(date).startOf('week');
    return Array.from({ length: 7 }, (_, i) => {
      const day = moment(startOfWeek).add(i, 'days');
      return {
        date: day.format('YYYY-MM-DD'),
        dayName: day.format('ddd'),
        dayNum: day.format('DD'),
        isToday: day.isSame(moment(), 'day'),
      };
    });
  }, [date]);

  // Group appointments by day and hour
  const appointmentsByDayHour = useMemo(() => {
    const grouped = {};
    appointments?.forEach(apt => {
      if (apt.status === 'Cancelled') return;
      if (!apt.property && !apt.propertyId) return; // Skip BLOCK TIME (no property)
      const day = moment(apt.startTime).format('YYYY-MM-DD');
      const hour = moment(apt.startTime).hour();
      const key = `${day}-${hour}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(apt);
    });
    return grouped;
  }, [appointments]);

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Grid container spacing={0}>
        {/* Time column */}
        <Grid item sx={{ width: 60 }}>
          <Box sx={{ height: 50, borderBottom: '1px solid #e0e0e0' }} />
          {timeSlots.map((slot) => (
            <Box
              key={slot.index}
              sx={{
                height: 40,
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                color: '#666',
              }}
            >
              {slot.time12.replace(':00 ', ' ')}
            </Box>
          ))}
        </Grid>

        {/* Day columns */}
        {weekDays.map((day) => (
          <Grid item key={day.date} sx={{ flex: 1, minWidth: 100 }}>
            {/* Day header */}
            <Box
              sx={{
                height: 50,
                borderBottom: '1px solid #e0e0e0',
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: day.isToday ? '#e3f2fd' : '#f5f5f5',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: day.isToday ? 600 : 400 }}>
                {day.dayName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  bgcolor: day.isToday ? '#1976d2' : 'transparent',
                  color: day.isToday ? '#fff' : 'inherit',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {day.dayNum}
              </Typography>
            </Box>

            {/* Hour slots */}
            {timeSlots.map((slot) => {
              const key = `${day.date}-${slot.hour}`;
              const apts = appointmentsByDayHour[key] || [];
              
              return (
                <Box
                  key={slot.index}
                  sx={{
                    height: 40,
                    borderBottom: '1px solid #f0f0f0',
                    borderLeft: '1px solid #e0e0e0',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    gap: '2px',
                    p: '2px',
                  }}
                >
                  {apts.slice(0, 3).map((apt, idx) => {
                    const tech = technicians?.find(t => t.tech_id === apt.user?.id);
                    return (
                      <Tooltip 
                        key={apt.id} 
                        title={<TooltipTitle info={apt} />} 
                        arrow 
                        placement="top"
                      >
                        <Link
                          href={`/appointments/${apt.id}`}
                          underline="none"
                          sx={{
                            flex: 1,
                            bgcolor: tech?.color || '#808080',
                            borderRadius: '4px',
                            color: '#fff',
                            fontSize: 9,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            lineHeight: 1.1,
                            p: '1px',
                            minWidth: 0,
                            position: 'relative',
                          }}
                        >
                          <Box sx={{ position: 'absolute', left: 1, top: 1, width: 8, height: 8 }}>
                            {apt.status === 'Active' && (
                              <CircleIcon sx={{ width: 8, height: 8, color: 'green' }} />
                            )}
                            {apt.status === 'In Progress' && (
                              <CircleIcon sx={{ width: 8, height: 8, color: 'orange' }} />
                            )}
                            {apt.status === 'Completed' && (
                              <CheckCircleIcon sx={{ width: 8, height: 8, color: 'green' }} />
                            )}
                          </Box>
                          {apt.reviewRequestSentAt && (
                            <Box sx={{
                              position: 'absolute', right: 1, top: 1,
                              width: 8, height: 8, borderRadius: '50%',
                              backgroundColor: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ fontSize: 5, fontWeight: 900, color: '#F4B400', lineHeight: 1 }}>G</Typography>
                            </Box>
                          )}
                          {apt.property?.zipCode || apt.type?.name?.substring(0, 3)}
                        </Link>
                      </Tooltip>
                    );
                  })}
                  {apts.length > 3 && (
                    <Typography variant="caption" sx={{ fontSize: 8, color: '#666' }}>
                      +{apts.length - 3}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default WeekView;
